import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { fetchProduct, fetchProducts } from "./shopAPI";
import { fetchCourses } from "../courses/coursesAPI";
import StorageService from "../../util/storage";
import { catchAllErrors } from "../../hoc/catchAllErrors";

export const fetchProductsAsync = createAsyncThunk("products/fetchProducts", async () => {
    try {
        const user = StorageService.getItem("user");

        const enrollments = await catchAllErrors(fetchCourses)(user.id);
        const response = await catchAllErrors(fetchProducts)();

        // filter enrolled courses as already purchased
        const products = response.map((product) => {
            const enrollment = enrollments.find((e) => e.attributes.course.data.id === product.id);
            return {
                ...product,
                attributes: {
                    ...product.attributes,
                    alreadyEnrolled: enrollment !== undefined && enrollment !== null,
                },
            };
        });

        return products;
    } catch (error) {
        toast("Failed to fetch products", { type: "error" });
        throw error;
    }
});

export const fetchProductItemAsync = createAsyncThunk("products/fetchProduct", async (id) => {
    try {
        const user = StorageService.getItem("user");
        const response = await catchAllErrors(fetchProduct)(id);
        const enrollments = await catchAllErrors(fetchCourses)(user.id);
        const enrollment = enrollments?.find((e) => e.attributes.course.data.id === response.id);

        return {
            ...response,
            attributes: {
                ...response.attributes,
                alreadyEnrolled: enrollment !== undefined && enrollment !== null,
                driverCourseId: enrollment?.id,
            },
        };
    } catch (error) {
        toast("Failed to fetch product", { type: "error" });
        console.log(error);
        throw error;
    }
});

export const productsSlice = createSlice({
    name: "products",
    initialState: {
        products: null,
        product: null,
        meta: {},
        status: "idle",
        error: null,
    },
    reducers: {
        resetStatus: (state) => {
            state.status = "idle";
            state.error = null;
        },
        setProducts: (state, action) => {
            state.products = action.payload;
        },
        setProduct: (state, action) => {
            state.product = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchProductsAsync.pending, (state) => {
                state.products = [];
                state.status = "pending";
            })
            .addCase(fetchProductsAsync.fulfilled, (state, action) => {
                state.products = action.payload;
                state.status = "fulfilled";
            })
            .addCase(fetchProductsAsync.rejected, (state, action) => {
                state.products = [];
                state.status = "rejected";
                state.error = action.error.message;
            })
            .addCase(fetchProductItemAsync.pending, (state) => {
                state.product = null;
                state.status = "pending";
            })
            .addCase(fetchProductItemAsync.fulfilled, (state, action) => {
                state.product = action.payload;
                state.status = "fulfilled";
            })
            .addCase(fetchProductItemAsync.rejected, (state, action) => {
                state.product = null;
                state.status = "rejected";
                state.error = action.error.message;
            });
    },
});

export const { setProducts, setProduct, resetStatus } = productsSlice.actions;

export const selectProducts = (state) => state.products.products;

export const selectProduct = (state) => state.products.product;

export default productsSlice.reducer;
